import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { RefreshLoadingModal } from '../RefreshLoadingModal';
import { useLegacyAuth } from '../../hooks/useLegacyAuth';
import './Leaderboard.css';

interface Score {
  id: string;
  score: number;
  player_name: string;
  user_id?: string; // New auth system user ID (registered user)
  twitter_user_id?: string;
  twitter_user?: {
    screen_name: string;
    user_id: string;
    profile_image_url_https?: string;
  };
  display_info?: {
    name: string;
    auth_type: 'twitter' | 'legacy_twitter' | 'wallet' | 'registered' | 'anonymous';
    twitter_handle?: string;
    profile_image_url?: string;
  };
  game_stats?: {
    highest_tile?: number;
    moves?: number;
    time_elapsed?: number;
  };
  replay_session_id?: string;
  created_at: string;
}

type FilterType = 'top' | 'recent' | 'my_games';

interface FilterConfig {
  displayName: string;
  endpoint: string;
  queryParams: Record<string, any>;
  style: { backgroundColor: string };
  visibility: boolean;
}

const Alien2048Leaderboard: React.FC = () => {
  const navigate = useNavigate();
  const [scores, setScores] = useState<Score[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('top');
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Get auth state via legacy hook (supports both new auth and legacy localStorage)
  const { twitterUser, userId } = useLegacyAuth();

  const FILTER_CONFIGS: Record<FilterType, FilterConfig> = {
    top: {
      displayName: 'Top Scores',
      endpoint: '/api/alien2048/scores/leaderboard',
      queryParams: { limit: 100 },
      style: { backgroundColor: '#52CDFE' }, // Alien blue
      visibility: true
    },
    recent: {
      displayName: 'Recent Plays',
      endpoint: '/api/alien2048/scores',
      queryParams: {
        sort_by: 'created_at',
        sort_order: 'desc',
        limit: 100
      },
      style: { backgroundColor: '#FEAC64' }, // Orange
      visibility: true
    },
    my_games: {
      displayName: 'My Games',
      // Use userId from new auth system if available, otherwise fall back to legacy twitterUser.user_id
      endpoint: userId
        ? `/api/alien2048/scores/user/${userId}/games`
        : twitterUser ? `/api/alien2048/scores/user/${twitterUser.user_id}/games` : '',
      queryParams: {
        sort_by: 'created_at',
        sort_order: 'desc',
        limit: 20,
        page: page
      },
      style: { backgroundColor: '#4CAF50' }, // Green
      visibility: !!(userId || twitterUser) // Visible when user is signed in (new auth or legacy)
    }
  };

  const fetchData = useCallback(async () => {
    const currentConfig = FILTER_CONFIGS[filter];
    if (!currentConfig.visibility) return;

    setLoading(true);
    setError(null);

    try {
      const queryString = new URLSearchParams(currentConfig.queryParams).toString();
      const url = `${currentConfig.endpoint}${queryString ? '?' + queryString : ''}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch scores');
      }

      const data = await response.json();

      // Handle different response formats
      const scores = data.scores || [];

      // Debug logging: show full score data including display_info
      console.log('[Alien2048 Leaderboard] Received scores:', {
        filter,
        count: scores.length,
        sampleScores: scores.slice(0, 3).map((s: Score) => ({
          id: s.id,
          score: s.score,
          player_name: s.player_name,
          user_id: s.user_id,
          twitter_user_id: s.twitter_user_id,
          twitter_user: s.twitter_user,
          display_info: s.display_info,
          game_stats: s.game_stats
        }))
      });

      if (filter === 'my_games') {
        setScores(scores);
        setTotalPages(data.totalPages || 1);
        setHasNext(data.hasNext || false);
        setHasPrev(data.hasPrev || false);
      } else if (filter === 'top') {
        setScores(scores);
      } else {
        setScores(scores);
      }
    } catch (err) {
      console.error('Error fetching scores:', err);
      setError('Failed to load scores. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filter, page, twitterUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setShowModal(true);
    await fetchData();
    setTimeout(() => setShowModal(false), 500);
  };

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
    setPage(1); // Reset to first page
  };

  // Note: Row click disabled - Alien 2048 does not have replay viewer support
  // const handleRowClick = (score: Score) => {
  //   if (score.replay_session_id) {
  //     navigate(`/alien2048/replay/${score.replay_session_id}`);
  //   }
  // };

  const handleBack = () => {
    navigate('/alien2048/');
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const currentConfig = FILTER_CONFIGS[filter];

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="alien2048-leaderboard-container">
        <div className="alien2048-leaderboard-header">
          <h1>Alien 2048 Leaderboard</h1>
          <button className="back-button" onClick={handleBack}>
            ← Back
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="alien2048-filter-tabs">
          {(Object.keys(FILTER_CONFIGS) as FilterType[]).map((key) => {
            const config = FILTER_CONFIGS[key];
            if (!config.visibility) return null;

            return (
              <button
                key={key}
                className={`alien2048-filter-tab ${filter === key ? 'active' : ''}`}
                style={filter === key ? config.style : {}}
                onClick={() => handleFilterChange(key)}
              >
                {config.displayName}
              </button>
            );
          })}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="alien2048-loading">
            <div className="alien2048-spinner"></div>
            <p>Loading scores...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="alien2048-error">
            <p>{error}</p>
            <button onClick={fetchData}>Try Again</button>
          </div>
        )}

        {/* Scores Table */}
        {!loading && !error && scores.length > 0 && (
          <div className="alien2048-scores-table">
            <table>
              <thead>
                <tr>
                  {filter === 'top' && <th>Rank</th>}
                  <th>Name</th>
                  <th>Score</th>
                  <th>Highest Tile</th>
                  <th>Moves</th>
                  <th>Time</th>
                  {(filter === 'recent' || filter === 'my_games') && <th>When</th>}
                </tr>
              </thead>
              <tbody>
                {scores.map((score, index) => {
                  // Use display_info.twitter_handle (from server enrichment) or fall back to legacy twitter_user
                  const twitterScreenName = score.display_info?.twitter_handle || score.twitter_user?.screen_name;
                  const isRegisteredUser = !!(score.user_id || score.twitter_user_id);

                  return (
                    <tr
                      key={score.id}
                    >
                      {filter === 'top' && <td>{index + 1}</td>}
                      <td className="player-name">
                        {twitterScreenName ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            <a
                              href={`https://x.com/${twitterScreenName}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="x-logo-fallback"
                            >
                              <img
                                src="/images/x-logo-black.png"
                                alt="X"
                                className="x-logo-fallback-img"
                              />
                            </a>
                            <span className="verified-check">✓</span>
                            <a
                              href={`https://x.com/${twitterScreenName}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ color: 'inherit', textDecoration: 'none' }}
                            >
                              @{twitterScreenName}
                            </a>
                          </div>
                        ) : isRegisteredUser ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            <span className="verified-check">✓</span>
                            <span>{score.player_name || 'anonymous'}</span>
                          </div>
                        ) : (
                          score.player_name || 'anonymous'
                        )}
                      </td>
                      <td className="score-value">{score.score.toLocaleString()}</td>
                      <td>{score.game_stats?.highest_tile || 2}</td>
                      <td>{score.game_stats?.moves || 0}</td>
                      <td>{formatTime(score.game_stats?.time_elapsed || 0)}</td>
                      {(filter === 'recent' || filter === 'my_games') && <td className="date-cell">{formatDate(score.created_at)}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && scores.length === 0 && (
          <div className="alien2048-empty-state">
            <p>No scores yet.</p>
          </div>
        )}

        {/* Pagination for My Games */}
        {filter === 'my_games' && !loading && scores.length > 0 && (
          <div className="alien2048-pagination">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={!hasPrev}
              className="pagination-btn"
            >
              ← Previous
            </button>
            <span className="page-info">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!hasNext}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        )}

        {/* Refresh Modal */}
        <RefreshLoadingModal show={showModal} />
      </div>
    </PullToRefresh>
  );
};

export default Alien2048Leaderboard;
